import type { PlaybookTree } from "@repo/app-types/entities/playbook";
import {
  computePlaybookChanges,
  formatPlaybookChanges,
} from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { LoadingText } from "@repo/ui/Buttons/loadingText";
import { Size } from "@repo/ui/helpers/helpers";
import { popoverStyle } from "@repo/ui/popover/style";
import { IconChevronDown } from "@tabler/icons-react";
import type { FC } from "react";
import { Dialog, DialogTrigger, Popover } from "react-aria-components";

/** Describes all the individual changes that can be published */
export interface PublishableChanges {
  identityNameChanged: boolean;
  identityRoleChanged: boolean;
  identityAdditionalContextChanged: boolean;
  toolIdsChanged: boolean;
  /** Static-agent prompt sections (Instructions tab) changed. */
  instructionSectionsChanged?: boolean;
  /** Calling hours, retry policy or inbound handling changed. */
  callingSettingsChanged?: boolean;
  dataExtractionChanged: boolean;
  playbookChanged: boolean;
  knowledgeBasesChanged: boolean;
  outcomeChanged: boolean;
  summaryTemplateChanged?: boolean;
  /** Required when playbookChanged is true to compute detailed changes */
  playbookInitialTemplate?: PlaybookTree;
  /** Required when playbookChanged is true to compute detailed changes */
  playbookCurrentTemplate?: PlaybookTree;
}

export interface PublishButtonProps {
  changes: PublishableChanges;
  hasChanges: boolean;
  isDisabled: boolean;
  isLoading: boolean;
  /** Why publishing is blocked, shown in place of the change list. */
  blockedReason?: string | null;
  onPublish: () => Promise<void>;
}

export const PublishButton: FC<PublishButtonProps> = ({
  changes,
  hasChanges,
  isDisabled,
  isLoading,
  blockedReason,
  onPublish,
}) => {
  const playbookChangeDescriptions =
    changes.playbookChanged &&
    changes.playbookInitialTemplate &&
    changes.playbookCurrentTemplate
      ? (() => {
          const playbookChanges = computePlaybookChanges(
            changes.playbookInitialTemplate,
            changes.playbookCurrentTemplate,
          );
          return playbookChanges
            ? formatPlaybookChanges(playbookChanges)
            : ["Playbook updated"];
        })()
      : changes.playbookChanged
        ? ["Playbook updated"]
        : [];

  return (
    <DialogTrigger>
      <Button
        variant={ButtonVariant.Primary}
        size={Size.MEDIUM}
        isDisabled={!hasChanges || isDisabled}
        isLoading={isLoading}
      >
        <span className="flex items-center gap-1">
          {isLoading ? "Publishing..." : "Publish"}
          <IconChevronDown className="h-4 w-4" />
        </span>
      </Button>
      <Popover placement="bottom end" className={popoverStyle().popover()}>
        <Dialog className="w-80 p-4 outline-hidden">
          {({ close }) => (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Publish changes
                </h3>
              </div>

              {blockedReason ? (
                <p className="text-xs text-destructive">{blockedReason}</p>
              ) : hasChanges ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-foreground">
                    Changes to publish:
                  </p>
                  <ul className="space-y-1 text-xs text-gray-600">
                    {changes.identityNameChanged && (
                      <li className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        Agent name updated
                      </li>
                    )}
                    {changes.identityRoleChanged && (
                      <li className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        Agent role updated
                      </li>
                    )}
                    {changes.identityAdditionalContextChanged && (
                      <li className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        Customer description updated
                      </li>
                    )}
                    {changes.instructionSectionsChanged && (
                      <li className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        Instructions updated
                      </li>
                    )}
                    {changes.toolIdsChanged && (
                      <li className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        Agent tools updated
                      </li>
                    )}
                    {changes.callingSettingsChanged && (
                      <li className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        Calling settings updated
                      </li>
                    )}
                    {changes.dataExtractionChanged && (
                      <li className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        Data extraction fields updated
                      </li>
                    )}
                    {changes.knowledgeBasesChanged && (
                      <li className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        Knowledge bases updated
                      </li>
                    )}
                    {changes.outcomeChanged && (
                      <li className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        Outcome updated
                      </li>
                    )}
                    {changes.summaryTemplateChanged && (
                      <li className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        Summary template updated
                      </li>
                    )}
                    {playbookChangeDescriptions.map((description, index) => (
                      <li
                        key={`playbook-change-${index}`}
                        className="flex items-center gap-2"
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
                        {description}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No changes to publish.
                </p>
              )}

              <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
                <Button
                  variant={ButtonVariant.Secondary}
                  size={Size.MEDIUM}
                  onClick={close}
                >
                  Close
                </Button>
                <Button
                  variant={ButtonVariant.Primary}
                  size={Size.MEDIUM}
                  onClick={() => {
                    close();
                    void onPublish();
                  }}
                  isLoading={isLoading}
                  isDisabled={
                    isDisabled || !hasChanges || Boolean(blockedReason)
                  }
                >
                  <LoadingText isLoading={isLoading} stages={["Publishing..."]}>
                    Publish
                  </LoadingText>
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};
